const API_BASE = '/api/v1/reading';

const currentPage = () => window.location.pathname;

async function readingError(response) {
  let message = `Server error: ${response.status}`;
  try {
    const payload = await response.json();
    if (typeof payload.detail === 'string' && payload.detail) message = payload.detail;
  } catch {
    // the body was not JSON
  }
  return message;
}

export async function askCards(question, { language, fromChat = false, onCards, onToken, onDone, onError }) {
  try {
    const response = await fetch(`${API_BASE}/ask`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        question,
        from_chat: Boolean(fromChat),
        page: currentPage(),
        ...(language ? { language } : {}),
      }),
    });

    if (!response.ok) throw new Error(await readingError(response));

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent = '';
    let doneFired = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (currentEvent === 'cards') onCards(data);
            else if (currentEvent === 'token') onToken(data.token);
            else if (currentEvent === 'done' && !doneFired) {
              doneFired = true;
              onDone();
            }
          } catch {
            // skip malformed
          }
        }
      }
    }

    if (!doneFired) onDone();
  } catch (err) {
    onError(err.message || 'Something went wrong');
  }
}

export async function askCardsFull(question, { language, onReading, onCards, onToken, onDone, onError }) {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE}/ask-full`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ question, page: currentPage(), ...(language ? { language } : {}) }),
    });

    if (!response.ok) throw new Error(await readingError(response));

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent = '';
    let doneFired = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (currentEvent === 'reading') onReading?.(data);
            else if (currentEvent === 'cards') onCards(data);
            else if (currentEvent === 'token') onToken(data.token);
            else if (currentEvent === 'done' && !doneFired) {
              doneFired = true;
              onDone();
            }
          } catch {
            // skip malformed
          }
        }
      }
    }

    if (!doneFired) onDone();
  } catch (err) {
    onError(err.message || 'Something went wrong');
  }
}

async function readInterpreterStream(response, { onToken, onDone, onError }) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = '';
  let doneFired = false;
  let failed = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (line.startsWith('event: ')) currentEvent = line.slice(7).trim();
      else if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.slice(6));
          if (currentEvent === 'token') onToken(data.token || '');
          else if (currentEvent === 'error') failed = data.message || 'The reading stopped.';
          else if (currentEvent === 'done' && !doneFired) {
            doneFired = true;
            if (!failed) onDone(data || {});
          }
        } catch {
          // skip a partial line
        }
      }
    }
  }
  if (failed) onError(failed);
  else if (!doneFired) onDone({});
}

export async function interpretSpread(body, handlers) {
  try {
    const response = await fetch(`${API_BASE}/interpret`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, page: currentPage() }),
    });
    if (!response.ok) {
      let message = 'The reading stopped. Your cards are still here. Try again.';
      try {
        const payload = await response.json();
        if (typeof payload.detail === 'string' && payload.detail) message = payload.detail;
      } catch {
        // the body was not JSON
      }
      handlers.onError(message);
      return;
    }
    await readInterpreterStream(response, handlers);
  } catch (err) {
    handlers.onError(err.message || 'Something went wrong');
  }
}

export async function askInterpreter(session, question, handlers) {
  try {
    const response = await fetch(`${API_BASE}/interpret/ask`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session, question, page: currentPage() }),
    });
    if (!response.ok) {
      let message = 'That answer stopped early. Ask again.';
      try {
        const payload = await response.json();
        if (typeof payload.detail === 'string' && payload.detail) message = payload.detail;
      } catch {
        // the body was not JSON
      }
      handlers.onError(message);
      return;
    }
    await readInterpreterStream(response, handlers);
  } catch (err) {
    handlers.onError(err.message || 'Something went wrong');
  }
}
